import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import moment from 'moment';
import _ from 'lodash';
import Draggable from 'react-draggable';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting.src';

import withChartExport from '../../../components/ChartExporting';
import { saveConfig, updateLineChart, updateWipBreakdownType } from '../../../components/Filter/FilterActions';
import { getWipBreakdownData } from '../CTAActions';
import dateUtils from '../../../util/dates';
import throttle from '../../../util/throttle';
import { escapeHTML } from '../../../util/text';

import expandIcon from '../../../assets/img/zoom-icon-off.svg';
import collapseIcon from '../../../assets/img/zoom-icon-on.svg';
import pieIcon from '../../../assets/img/pie-icon.svg';
import lineChartIcon from '../../../assets/img/line-chart-icon-on.svg';
import arrowDownIcon from '../../../assets/img/arrow_down.svg';

import highchartFactory from '../../../util/highcharts';
const highchartUtils = highchartFactory(Highcharts);

const config = {
  chart: {
    type: 'spline',
    backgroundColor: '#fff'
  },
  exporting: {
    enabled: false
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  legend: {
    enabled: false
  },
  colors: ['#0086b3'],
  tooltip: {
    shared: true,
    useHTML: true,
    formatter: function() {}
  },
  xAxis: {
    type: 'datetime',
    gridLineWidth: 0,
    ordinal: false,
    showLastTickLabel: true,
    title: {
      text: null
    },
    plotLines: [
      {
        value: 0
      }
    ]
  },
  yAxis: {
    gridLineWidth: 0,
    labels: {
      enabled: false
    },
    title: {
      text: null
    },
    showFirstLabel: false
  },
  plotOptions: {
    spline: {
      marker: {
        enabledThreshold: 2,
        lineWidth: 1,
        radius: 5
      },
      states: {
        hover: {
          lineWidth: 2,
          halo: {
            opacity: 0
          },
          marker: {
            enabled: true
          }
        }
      }
    }
  },
  series: [
    {
      marker: {
        symbol: 'circle'
      },
      name: 'WIP',
      data: []
    }
  ]
};

const pieConfig = {
  chart: {
    backgroundColor: '#fff',
    type: 'pie',
    spacing: [0, 0, 0, 0]
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  exporting: {
    enabled: false
  },
  legend: {
    enabled: true,
    width: 230,
    layout: 'vertical',
    align: 'left',
    verticalAlign: 'top',
    itemMarginTop: 4,
    itemMarginBottom: 4,
    itemStyle: {
      fontSize: '14px',
      fontWeight: 'normal',
      color: 'rgba(0, 0, 0, 0.87)'
    }
  },
  tooltip: {
    useHTML: true,
    formatter: function() {}
  },
  xAxis: {
    type: 'category'
  },
  plotOptions: {
    pie: {
      innerSize: 0,
      allowPointSelect: false,
      cursor: 'pointer',
      point: {
        events: {
          click: function() {}
        }
      },
      dataLabels: {
        enabled: false,
        useHTML: true,
        formatter: function() {},
        style: {
          fontWeight: 'normal',
          fontSize: '12px'
        }
      }
    }
  },
  series: []
};

export class DailyWIP extends React.Component {
  static propTypes = {
    day: PropTypes.number,
    data: PropTypes.array,
    dateFormat: PropTypes.string,
    dashboard: PropTypes.object,
    allLoaded: PropTypes.bool,
    wipBreakdownType: PropTypes.string,
    wipBreakdownBy: PropTypes.string,
    wipBreakdownData: PropTypes.array,
    wipBreakdownLoading: PropTypes.bool,
    onSegmentClick: PropTypes.func,
    chartExporting: PropTypes.object,
    close: PropTypes.func,
    saveConfig: PropTypes.func,
    setWipBreakdownType: PropTypes.func,
    getWipBreakdownData: PropTypes.func
  };

  static defaultProps = {
    day: 0
  };

  constructor(props) {
    super(props);

    this.state = {
      isFullScreen: false,
      width: undefined,
      height: undefined,
      pos: { x: 95, y: 112 },
      currentWip: 0,
      data: [],
      isLabelsVisible: true
    };

    this.handleClose = this.handleClose.bind(this);
    this.calculateChartData = throttle(this._calculateChartData, 500, this);
  }

  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    config.exporting = { ...config.exporting, ...this.props.chartExporting };
    pieConfig.exporting = { ...pieConfig.exporting, ...this.props.chartExporting };

    this.initChart();

    const isPieEnabled = this.props.wipBreakdownType === 'pie';
    const dataReady = this.props.allLoaded && this.props.data?.length;
    if (this.props.data && this.props.data.length) {
      if (!isPieEnabled) {
        this.updateDateFormat(this.props.dateFormat);
        this.calculateChartData();
      } else if (dataReady) {
        this.props.getWipBreakdownData(this.props.data, this.props.day, this.props.wipBreakdownBy);
      }
    }
  }

  componentDidUpdate(prevProps) {
    const isPieEnabled = this.props.wipBreakdownType === 'pie';
    const wasPieEnabled = prevProps.wipBreakdownType === 'pie';

    if (wasPieEnabled !== isPieEnabled) {
      this.switchChartType();
    }

    if (!isPieEnabled) {
      if (prevProps.data != this.props.data) {
        this.calculateChartData();
      }

      if (prevProps.dateFormat != this.props.dateFormat) {
        this.updateDateFormat(this.props.dateFormat);
      }

      if (prevProps.day !== this.props.day) {
        this.updateCurrentWip();
      }
    } else {
      const dataReady = this.props.allLoaded && this.props.data?.length;
      const dataChanged = prevProps.data !== this.props.data;
      const dayChanged = prevProps.day !== this.props.day;
      const breakdownByChanged = prevProps.wipBreakdownBy !== this.props.wipBreakdownBy;
      const justLoaded = !prevProps.allLoaded && this.props.allLoaded;

      if (dataReady && (dataChanged || dayChanged || breakdownByChanged || justLoaded)) {
        this.props.getWipBreakdownData(this.props.data, this.props.day, this.props.wipBreakdownBy);
      }

      if (prevProps.wipBreakdownData !== this.props.wipBreakdownData) {
        this.updatePieChartData();
      }
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  initChart = () => {
    const isPieChart = this.props.wipBreakdownType === 'pie';
    const chartConfig = isPieChart ? pieConfig : config;
    const that = this;

    if (isPieChart) {
      chartConfig.tooltip.formatter = function() {
        return that.pieTooltipFormatter(this);
      };
      chartConfig.plotOptions.pie.dataLabels.formatter = function() {
        return that.pieDataLabelFormatter(this);
      };
      chartConfig.plotOptions.pie.point.events.click = function() {
        that.onPieSegmentClick(this);
      };
    } else {
      chartConfig.tooltip.formatter = function() {
        return that.tooltipFormatter(this, that);
      };
    }

    this.chart = new Highcharts.Chart('daily_wip_chart_container', chartConfig);
  };

  switchChartType = () => {
    this.chart.destroy();
    this.initChart();

    const isPieEnabled = this.props.wipBreakdownType === 'pie';
    const dataReady = this.props.allLoaded && this.props.data?.length;
    if (isPieEnabled && dataReady) {
      this.props.getWipBreakdownData(this.props.data, this.props.day, this.props.wipBreakdownBy);
    } else if (this.props.data && this.props.data.length) {
      this.updateDateFormat(this.props.dateFormat);
      this.calculateChartData();
    }

    this.updateFontSize();
  };

  updatePieChartData = () => {
    if (!this.chart || !this.props.wipBreakdownData) {
      return;
    }

    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }

    const breakdownData = this.props.wipBreakdownData;
    const total = _.sumBy(breakdownData, 'y');
    this.setState({ currentWip: total });

    this.chart.addSeries({
      legendType: 'point',
      showInLegend: true,
      colorByPoint: true,
      data: [...breakdownData]
    }, true);
  };

  pieTooltipFormatter(ev) {
    const name = escapeHTML(ev.key);
    return `<div class="tooltip-label-item tooltip-label-box" style="color: #001A33"><span class="label-box" style="background-color: ${ev.color}";></span><strong>${name}: </strong> (${ev.y} ${ev.y === 1 ? 'item' : 'items'}) ${ev.percentage.toFixed(2)}%</div>`;
  }

  pieDataLabelFormatter(ev) {
    const name = escapeHTML(ev.key);
    const number = ev.y;
    const percentage = ev.percentage.toFixed(2);
    return `<div class="tooltip-label-item tooltip-label-box" style="color: #001A33"><strong>${name} (${percentage}%) </strong>${number} ${number === 1 ? 'item' : 'items'}</div>`;
  }

  onPieSegmentClick = (point) => {
    const segmentName = point.name;
    const segmentData = this.props.wipBreakdownData.find(d => d.name === segmentName);

    if (segmentData && segmentData.items && this.props.onSegmentClick) {
      this.props.onSegmentClick({
        name: segmentName,
        count: segmentData.y,
        color: segmentData.color,
        items: segmentData.items
      });
    }
  };

  updateDateFormat(dateFormat) {
    this.chart.update({
      xAxis: {
        dateTimeLabelFormats: dateUtils.createHighchartFormats(dateFormat)
      }
    });
  }

  updateCurrentWip = () => {
    const point = this.state.data[this.props.day];
    if (!point) {
      return false;
    }

    this.setState({
      currentWip: point.y
    });
  };

  _calculateChartData() {
    if (!this.chart || !this.chart.update || !this.chart.options) {
      return false;
    }

    const data = this.generateData(this.props.data);
    if (!data.length) {
      return false;
    }

    this.setState({ data, currentWip: data.length ? _.last(data).y : 0 });

    this.chart.update({
      series: [
        {
          data
        }
      ],
      xAxis: {
        plotLines: [
          {
            color: '#F5F5F5',
            width: 1,
            value: data[0].x,
            label: {
              text: ''
            }
          }
        ]
      }
    });
  }

  generateData = data => {
    const chartData = _(data)
      .flatMap(i => {
        return _.flatMap(i.series, 'data');
      })
      .sortBy('lastDate')
      .groupBy(i => moment(i.lastDate).startOf('day'))
      .map((i, date) => {
        return {
          x: +moment(date).local(),
          y: i.length
        };
      })
      .value();

    _(data)
      .filter(d => {
        return !d.series.length;
      })
      .forEach(d => {
        chartData.push({
          x: +moment(d.date).local(),
          y: 0
        });
      });

    return _.sortBy(chartData, 'x');
  };

  updateFontSize = () => {
    if (!this.chart) return;

    const isFullscreen = this.state.isFullScreen;
    const { isLabelsVisible } = this.state;
    const isPieChart = this.props.wipBreakdownType === 'pie';

    if (isPieChart) {
      this.chart.update({
        exporting: {
          enabled: isFullscreen,
          sourceWidth: 1000,
          sourceHeight: 600,
          chartOptions: {
            chart: {
              spacingTop: 50,
              spacingRight: 180,
              spacingBottom: 50,
              spacingLeft: 180
            },
            title: {
              text: 'WIP Breakdown',
              style: {
                fontSize: '16px',
                fontWeight: 'bold'
              }
            },
            legend: {
              enabled: false
            },
            plotOptions: {
              pie: {
                size: '55%',
                center: ['50%', '50%'],
                dataLabels: {
                  enabled: true,
                  useHTML: false,
                  distance: 25,
                  crop: false,
                  overflow: 'allow',
                  format: '<b>{point.name}</b>: {point.percentage:.1f}% ({point.y} item{#if (point.y != 1)}s{/if})',
                  style: {
                    color: 'rgba(0, 0, 0, 0.87)',
                    fontSize: '12px',
                    fontFamily: 'Poppins',
                    textOverflow: 'ellipsis'
                  }
                }
              }
            }
          }
        },
        legend: {
          enabled: isLabelsVisible
        },
        plotOptions: {
          pie: {
            size: isFullscreen ? '60%' : '75%',
            dataLabels: isFullscreen ? {
              enabled: true,
              useHTML: true,
              connectorWidth: 2,
              distance: 20,
              crop: false,
              overflow: 'justify',
              style: {
                fontSize: '16px',
                fontWeight: 'normal',
                textOutline: 'none',
                whiteSpace: 'normal'
              }
            } : {
              enabled: false,
              style: {
                fontSize: '11px'
              }
            }
          }
        }
      });
    } else {
      const opt = {
        exporting: {
          enabled: isFullscreen,
          chartOptions: {
            title: {
              text: 'WIP',
              style: {
                fontSize: '16px',
                fontWeight: 'bold'
              }
            }
          }
        },
        plotOptions: {
          spline: {
            dataLabels: {
              style: {
                fontSize: isFullscreen ? '16px' : '11px',
                fontWeight: 'bold',
                color: 'contrast'
              }
            }
          }
        }
      };

      if (isFullscreen) {
        opt.yAxis = {
          gridLineWidth: 1,
          labels: {
            enabled: true
          }
        };
      } else {
        opt.yAxis = {
          gridLineWidth: 0,
          labels: {
            enabled: false
          }
        };
      }

      this.chart.update(opt);
    }

    this.chart.reflow();
  };

  handleToggleChartType = () => {
    const newType = this.props.wipBreakdownType === 'pie' ? 'line' : 'pie';
    this.props.setWipBreakdownType(newType);
    this.props.saveConfig();
  };

  handleHideLabels = () => {
    this.setState({ isLabelsVisible: !this.state.isLabelsVisible }, () => this.updateFontSize());
  };

  handleClose() {
    this.props.close();
    this.props.saveConfig();
  }

  handleDrag = (e, data) => {
    this.setState({ pos: { x: data.x, y: data.y } });
  };

  handleResize = () => {
    if (this.state.isFullScreen) {
      this.setState(
        {
          width: undefined,
          height: undefined,
          isFullScreen: false,
          pos: { x: 95, y: 112 }
        },
        () => this.updateFontSize()
      );
    } else {
      this.setState(
        {
          width: '100%',
          height: '100%',
          isFullScreen: true,
          pos: { x: 0, y: 0 }
        },
        () => this.updateFontSize()
      );
    }
  };

  tooltipFormatter(chartScope, componentScope) {
    let tooltipHtml = '';
    const s = [];
    tooltipHtml += `<div class="tooltip-label-item">${moment(
      chartScope.x
    ).format(componentScope.props.dateFormat)}</div>`;
    tooltipHtml += `<div class="tooltip-label-item">${
      chartScope.y
    } work item${chartScope.y != 1 ? 's' : ''}</div>`;
    s.push(tooltipHtml);
    return s;
  }

  render() {
    const isPieChart = this.props.wipBreakdownType === 'pie';

    const tooltipChartType = (
      <Tooltip id="chart-type-tooltip" className="tooltip_dark">
        {isPieChart ? 'Show line chart' : 'Show breakdown'}
      </Tooltip>
    );

    const tooltipLabels = (
      <Tooltip id="labels-tooltip" className="tooltip_dark">
        {this.state.isLabelsVisible ? 'Hide labels' : 'Show labels'}
      </Tooltip>
    );

    return (
      <Draggable
        defaultPosition={{ x: 95, y: 112 }}
        position={this.state.pos}
        onStop={this.handleDrag}
        axis="both"
        bounds="parent"
      >
        <div
          className={`box_white_shadow chart_averages_box ${isPieChart ? 'breakdown' : ''} ${this.state.isFullScreen ? 'fullscreen' : ''}`}
          style={{ width: this.state.width, height: this.state.height }}
        >
          <div className="box_white_title mb-4">
            <h3>{isPieChart ? 'WIP Breakdown' : 'WIP'}</h3>
            {isPieChart && (
              <OverlayTrigger placement="left" overlay={tooltipLabels}>
                <div className="labels-toggle" onClick={this.handleHideLabels}>
                  <img
                    src={arrowDownIcon}
                    alt="toggle labels"
                    style={{ transform: this.state.isLabelsVisible ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  />
                </div>
              </OverlayTrigger>
            )}
            <div className="right-actions">
              <OverlayTrigger placement="top" overlay={tooltipChartType}>
                <Button variant="secondary" size="sm" className="btn-action" onClick={this.handleToggleChartType}>
                  <img src={isPieChart ? lineChartIcon : pieIcon} alt="toggle chart type" />
                </Button>
              </OverlayTrigger>
              <Button
                variant="secondary"
                size="sm"
                className={`btn-action ${this.state.isFullScreen ? 'fullscreen' : ''}`}
                onClick={this.handleResize}
              >
                <img src={this.state.isFullScreen ? expandIcon : collapseIcon} alt="toggleFullScreen" />
              </Button>
              <Button className="btn-action" onClick={this.handleClose}>
                &times;
              </Button>
            </div>
          </div>
          <p>
            {this.state.currentWip}
            {' '}
            work item
            {this.state.currentWip != 1 ? 's' : ''}
          </p>

          <div id="daily_wip_chart_container" />
        </div>
      </Draggable>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    close: () => dispatch(updateLineChart(false, 'cta')),
    saveConfig: () => dispatch(saveConfig()),
    setWipBreakdownType: type => dispatch(updateWipBreakdownType(type, 'cta')),
    getWipBreakdownData: (data, day, breakdownBy) => dispatch(getWipBreakdownData(data, day, breakdownBy))
  };
};

const mapStateToProps = state => {
  return {
    dateFormat: state.filters.dateFormat,
    dashboard: state.dashboards.selected,
    allLoaded: state.cta.allLoaded,
    wipBreakdownType: state.filters.cta.wipBreakdownType || 'line',
    wipBreakdownBy: state.filters.cta.wipBreakdownBy || 'lists',
    wipBreakdownData: state.cta.wipBreakdown,
    wipBreakdownLoading: state.cta.wipBreakdownLoading
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(DailyWIP));
